'use client';
import { RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useClickOutside } from '@/hooks/useClickOutside';
import { cn } from '@/lib/utils';

import Card from '@/components/atoms/Card';
import { ModalCloseButton } from '@/components/atoms/Modal/Components/ModalCloseButton';
import { Overlay } from '@/components/atoms/Overlay';
import Section from '@/components/atoms/Section/Section';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { ModalProps } from './types';

export { type ModalProps };

export function Modal(props: ModalProps) {
  const {
    isOpen,
    children,
    hasClose = true,
    closeIsOutside = false,
    backgroundClickable = false,
    closeOnOutsideClick = true,
    onClose = () => {},
    classNames,
    fullSizeMobile,
    overlayProps = {},
    styles,
    lockScroll,
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const lastScrollPosition = useRef<number>(0);

  const [mounted, setMounted] = useState<boolean>(false);

  useClickOutside({
    ref: ref as RefObject<HTMLDivElement>,
    callback: () => {
      if (!closeOnOutsideClick) return;

      if (!isOpen) return;
      onCloseModal();

      if (onClose) {
        onClose();
      }
    },
    excludeDataAttr: ['data-header-blur-background', 'data-modal'],
  });

  const onCloseModal = () => {
    if (!lockScroll) return;

    const body = document.body;
    const html = document.documentElement;

    body.classList.remove('remove-scroll');
    html.classList.add('!scroll-smooth');
    window.scrollTo({
      left: 0,
      behavior: 'instant',
      top: lastScrollPosition.current,
    });
  };

  const onOpenModal = () => {
    if (!lockScroll) return;

    const body = document.body;

    lastScrollPosition.current = window.scrollY;

    body.style.setProperty('--top-scroll', `-${window.scrollY}px`);
    body.classList.add('remove-scroll');
  };

  useEffect(() => {
    if (isOpen) onOpenModal();
    else mounted && onCloseModal();
  }, [isOpen]);

  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return (
    <>
      {createPortal(
        <Section
          className={cn(isOpen ? 'block' : 'hidden')}
          data-tracker-section={
            props['data-tracker-section'] ?? SECTION_NAMES.MODAL
          }
        >
          <Overlay
            {...overlayProps}
            classNames={{ root: cn(classNames?.overlay) }}
          />
          <div
            className={cn(
              'fixed left-0 top-0 z-50 flex items-center justify-center',
              !backgroundClickable && 'h-full w-full',
              classNames?.root
            )}
            ref={ref}
            data-tracker-component='modal'
            data-modal
            style={styles?.root}
          >
            {hasClose && closeIsOutside && (
              <ModalCloseButton
                handleClose={onClose}
                classNames={{
                  closeCross: classNames?.closeCross,
                  close: classNames?.close,
                }}
              />
            )}
            <Card
              data-tracker-component='modal'
              roudnessLevel='s'
              classNames={{
                root: cn(
                  'px-5 pb-8 pt-12 overflow-auto',
                  fullSizeMobile
                    ? 'm-0 h-[calc(100%_-_4rem)] md:h-auto md:m-5'
                    : 'm-5',
                  classNames?.modal
                ),
              }}
              style={styles?.modal}
            >
              {hasClose && !closeIsOutside && (
                <ModalCloseButton
                  handleClose={onClose}
                  classNames={{
                    closeCross: classNames?.closeCross,
                    close: classNames?.close,
                  }}
                  data-tracker-component='modal-close'
                />
              )}
              <div
                className={cn(
                  'h-full w-full overflow-auto',
                  classNames?.content
                )}
              >
                {children}
              </div>
            </Card>
          </div>
        </Section>,
        document.getElementById('page-wrapper') ?? document.body
      )}
    </>
  );
}
